import { PlayerStats, Decision, DecisionOption } from '../types/game';

export const calculateLifeScore = (stats: PlayerStats): number => {
  const weights = {
    health: 0.25,
    happiness: 0.25,
    wealth: 0.15,
    relationships: 0.15,
    education: 0.10,
    career: 0.10,
  };

  return Math.round(
    Object.entries(stats).reduce((total, [key, value]) => {
      return total + (value * (weights[key as keyof PlayerStats] || 0));
    }, 0)
  );
};

export const getLifeStageForAge = (age: number): string => {
  if (age < 18) return 'childhood';
  if (age < 25) return 'early-adulthood';
  if (age < 35) return 'young-adult';
  if (age < 50) return 'middle-age';
  if (age < 65) return 'mature-adult';
  return 'senior';
};

export const getCategoryColor = (category: string): string => {
  const colors = {
    health: '#ef4444',
    career: '#3b82f6',
    relationships: '#ec4899',
    education: '#8b5cf6',
    finance: '#10b981',
    lifestyle: '#f59e0b',
  };
  return colors[category as keyof typeof colors] || '#6b7280';
};

export const getCategoryGradient = (category: string): string => {
  const gradients = {
    health: 'from-red-500 to-pink-500',
    career: 'from-blue-500 to-cyan-500',
    relationships: 'from-purple-500 to-indigo-500',
    education: 'from-violet-500 to-purple-500',
    finance: 'from-green-500 to-emerald-500',
    lifestyle: 'from-orange-500 to-yellow-500',
  };
  return gradients[category as keyof typeof gradients] || 'from-slate-500 to-slate-600';
};

export const canMakeDecision = (decision: Decision, stats: PlayerStats, wealth: number): boolean => {
  // Check if decision has prerequisites
  if (decision.prerequisites && decision.prerequisites.length > 0) {
    // This would check if prerequisites are met
    // For now, we'll assume they are met
  }

  // Check if any option meets requirements (debt is always allowed)
  return decision.options.some(option => {
    const meetsRequirements = !option.requirements || 
      Object.entries(option.requirements).every(([stat, value]) => 
        stats[stat as keyof PlayerStats] >= (value || 0)
      );
    return meetsRequirements; // Always allow debt
  });
};

export const getStatChangeColor = (value: number): string => {
  if (value > 0) return 'text-success-600';
  if (value < 0) return 'text-danger-600';
  return 'text-slate-600';
};

export const getStatChangeIcon = (value: number): string => {
  if (value > 0) return '↗️';
  if (value < 0) return '↘️';
  return '→';
};

export const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

export const getRandomLifeEvent = (age: number): string => {
  const events = {
    childhood: [
      "You discovered a new hobby that brings you joy",
      "You made a lifelong friend at school",
      "You learned an important life lesson from your family",
    ],
    'early-adulthood': [
      "You had an inspiring conversation with a mentor",
      "You discovered your passion for a particular field",
      "You learned to manage your finances better",
    ],
    'young-adult': [
      "You received recognition for your hard work",
      "You strengthened an important relationship",
      "You made a wise investment decision",
    ],
    'middle-age': [
      "You achieved a major career milestone",
      "You helped someone in need",
      "You learned a valuable new skill",
    ],
    'mature-adult': [
      "You reflected on your life's journey",
      "You shared wisdom with younger generations",
      "You found peace with past decisions",
    ],
    senior: [
      "You celebrated another year of life",
      "You shared stories with loved ones",
      "You appreciated the simple joys of life",
    ],
  };

  const stage = getLifeStageForAge(age);
  const stageEvents = events[stage as keyof typeof events] || events.senior;
  return stageEvents[Math.floor(Math.random() * stageEvents.length)];
};

export const generateLifeInsight = (stats: PlayerStats, age: number): string => {
  const highestStat = Object.entries(stats).reduce((a, b) => stats[a[0] as keyof PlayerStats] > stats[b[0] as keyof PlayerStats] ? a : b);
  const lowestStat = Object.entries(stats).reduce((a, b) => stats[a[0] as keyof PlayerStats] < stats[b[0] as keyof PlayerStats] ? a : b);

  const insights = {
    health: "Your health is your greatest asset. Take care of your body and mind.",
    happiness: "Happiness comes from within. Focus on what truly matters to you.",
    wealth: "Financial security provides freedom, but don't let money control your life.",
    relationships: "Strong relationships are the foundation of a fulfilling life.",
    education: "Never stop learning. Knowledge is the key to growth and opportunity.",
    career: "A fulfilling career is about more than just money - it's about purpose.",
  };

  return insights[highestStat[0] as keyof typeof insights] || "Life is a journey of continuous growth and learning.";
};
